import { forgetStorefrontVisit } from '@/features/clubs/storefrontReturn'
import { api } from '@/lib/api/requests'
import { clearPkceState } from '@/lib/auth/pkce'
import { buildLogoutUrl } from '@/lib/auth/oauth'
import { clearStoredTokens } from '@/lib/auth/tokenStore'

/**
 * Full sign-out: end the SERVER session first, then clear local state and leave.
 *
 * POST /auth/logout is what actually clears the httpOnly refresh cookie (via
 * Set-Cookie) and runs Cognito GlobalSignOut. Without it, clearing only the
 * in-memory tokens leaves the refresh cookie in place, so the next page load
 * calls /auth/refresh and silently re-authenticates — "signed out, then bounced
 * back in." The call must run BEFORE clearStoredTokens(), while the access token
 * still exists, so GlobalSignOut can authenticate; the cookie is cleared either
 * way (the backend reads it from the request). Best-effort: on any failure we
 * still clear local state and redirect, since the user is leaving regardless.
 *
 * buildLogoutUrl() points at Cognito /logout?...&logout_uri=<our '/'>, which
 * also clears the Cognito hosted (Google SSO) session and lands the user back on
 * our home page — not an off-brand hosted login form.
 *
 * The club the tab walked out of is forgotten FIRST, synchronously (owner, 2026-10-16).
 * It now survives a reload (`features/clubs/storefrontReturn.ts`), and this exit is a
 * reload, so without this a shared device would keep the last person's club in the
 * header. Every sign-out path (the menus, the profile's security actions) comes
 * through here, which is why it lives here and not on a button.
 */
export async function logoutAndRedirect(): Promise<void> {
  forgetStorefrontVisit()
  try {
    await api.post<{ ok?: boolean }>('/auth/logout', { auth: 'include', handleAuthErrors: false })
  } catch {
    // Network error / already-invalid token — proceed with local cleanup + redirect.
  }
  clearStoredTokens()
  clearPkceState()
  if (typeof window !== 'undefined') {
    window.location.assign(buildLogoutUrl())
  }
}
